'use client';

/**
 * CartDrawer — drawer que muestra items agrupados por vendedor.
 *
 * Feature (v136): cada grupo de vendedor tiene su propio botón "Checkout"
 * que inicia el flujo de compra para esos items.
 *
 * TASK-FEAT-CART-UNIFIED-CHECKOUT (2026-11-05): ahora muestra el costo de
 * envío estimado por vendedor (vía `computeCartShipping`) + badge de "envío
 * gratis" cuando el subtotal alcanza el threshold. El footer también muestra
 * el total estimado (subtotal + shipping) en lugar de solo el subtotal.
 */

import { ShoppingBag, X, Trash2, Minus, Plus, Store, Truck } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { useCart, type CartItem } from '@/lib/cart/store';
import { CartCheckout } from './cart-checkout';
import { computeCartShipping } from './cart-pricing';
import { useMemo } from 'react';

export function CartDrawer() {
  const {
    items,
    itemsBySeller,
    totalBySeller,
    totalCount,
    grandTotal,
    isOpen,
    setIsOpen,
    removeItem,
    updateQuantity,
    clearAll,
  } = useCart();

  // TASK-FEAT-CART-UNIFIED-CHECKOUT (2026-11-05): compute per-seller
  // shipping quotes + the overall estimated grand total (Σ subtotals +
  // Σ shipping costs across all sellers, assuming ship method).
  const sellerQuotes = useMemo(() => {
    const map: Record<string, ReturnType<typeof computeCartShipping>> = {};
    for (const [sellerId, sellerItems] of Object.entries(itemsBySeller)) {
      map[sellerId] = computeCartShipping(sellerItems);
    }
    return map;
  }, [itemsBySeller]);

  const estimatedGrandTotal = useMemo(() => {
    let sum = 0;
    for (const sellerId of Object.keys(itemsBySeller)) {
      const quote = sellerQuotes[sellerId];
      if (quote) {
        sum += quote.grandTotal; // includes shipping if ship method
      } else {
        // No fulfillment data — fall back to subtotal.
        sum += parseFloat(totalBySeller[sellerId] ?? '0');
      }
    }
    return sum.toFixed(2);
  }, [itemsBySeller, sellerQuotes, totalBySeller]);

  if (!isOpen) return null;

  const sellerEntries = Object.entries(itemsBySeller);

  return (
    <>
      {/* Overlay */}
      <div
        className="fixed inset-0 bg-black/50 z-50"
        onClick={() => setIsOpen(false)}
        aria-hidden="true"
      />

      {/* Drawer */}
      <div
        className="fixed right-0 top-0 bottom-0 w-full max-w-md bg-background z-50 shadow-xl flex flex-col"
        role="dialog"
        aria-label="Carrito de compras"
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b">
          <div className="flex items-center gap-2">
            <ShoppingBag className="size-5" aria-hidden="true" />
            <h2 className="text-lg font-semibold">Carrito</h2>
            {totalCount > 0 && (
              <Badge variant="secondary">{totalCount}</Badge>
            )}
          </div>
          <div className="flex items-center gap-2">
            {items.length > 0 && (
              <Button variant="ghost" size="sm" onClick={clearAll}>
                Vaciar
              </Button>
            )}
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setIsOpen(false)}
              aria-label="Cerrar carrito"
            >
              <X className="size-5" aria-hidden="true" />
            </Button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4">
          {items.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <ShoppingBag className="size-12 mx-auto mb-3 opacity-30" aria-hidden="true" />
              <p>Tu carrito está vacío</p>
              <p className="text-xs mt-1">
                Agrega productos del marketplace para agruparlos por vendedor
              </p>
            </div>
          ) : (
            <div className="space-y-6">
              {sellerEntries.map(([sellerId, sellerItems]) => {
                const quote = sellerQuotes[sellerId];
                return (
                  <div key={sellerId} className="space-y-3">
                    {/* Seller header */}
                    <div className="flex items-center gap-2">
                      <Store className="size-4 text-muted-foreground" aria-hidden="true" />
                      <span className="text-sm font-medium">
                        {sellerItems[0]?.sellerName ?? 'Vendedor'}
                      </span>
                      <Badge variant="outline" className="text-xs">
                        {sellerItems.length}{' '}
                        {sellerItems.length === 1 ? 'producto' : 'productos'}
                      </Badge>
                    </div>

                    {/* Items */}
                    {sellerItems.map((item) => (
                      <CartLine
                        key={`${item.sellerId}-${item.productId}`}
                        item={item}
                        onRemove={() =>
                          removeItem(item.productId, item.sellerId)
                        }
                        onUpdateQty={(qty) =>
                          updateQuantity(item.productId, item.sellerId, qty)
                        }
                      />
                    ))}

                    {/* Seller total + checkout */}
                    <div className="flex items-center justify-between pl-2 gap-2">
                      <div className="flex flex-col text-sm">
                        <span className="text-muted-foreground">
                          Subtotal:{' '}
                          <span className="font-semibold text-foreground">
                            {totalBySeller[sellerId]} créditos
                          </span>
                        </span>
                        {/* TASK-FEAT-CART-UNIFIED-CHECKOUT (2026-11-05):
                            show shipping estimate inline. */}
                        {quote && quote.ships && (
                          <span className="text-xs text-muted-foreground flex items-center gap-1 mt-0.5">
                            <Truck className="size-3" aria-hidden="true" />
                            {quote.freeShippingApplied ? (
                              <span className="text-emerald-600 dark:text-emerald-400 font-medium">
                                Envío gratis incluido
                              </span>
                            ) : quote.freeShippingThreshold !== null ? (
                              <>
                                Envío {quote.shippingCost} cr · Gratis desde {quote.freeShippingThreshold} cr
                              </>
                            ) : (
                              <>Envío {quote.shippingCost} cr</>
                            )}
                          </span>
                        )}
                        {quote && !quote.ships && quote.pickup && (
                          <span className="text-xs text-muted-foreground">
                            Solo recogida en local
                          </span>
                        )}
                      </div>
                      <CartCheckout
                        sellerItems={sellerItems}
                        cartTotalCredits={totalBySeller[sellerId]}
                        sellerName={sellerItems[0]?.sellerName ?? ''}
                        onComplete={() => {
                          clearAll();
                          setIsOpen(false);
                        }}
                      />
                    </div>

                    <Separator />
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        {items.length > 0 && (
          <div className="border-t p-4 space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium">
                Total estimado{' '}
                <span className="text-xs font-normal text-muted-foreground">
                  (con envío)
                </span>
              </span>
              <span className="text-lg font-bold">{estimatedGrandTotal} créditos</span>
            </div>
            <p className="text-xs text-muted-foreground">
              El checkout se hace por vendedor. El costo final de envío puede
              variar según el método de entrega que elijas al confirmar la compra.
            </p>
          </div>
        )}
      </div>
    </>
  );
}

function CartLine({
  item,
  onRemove,
  onUpdateQty,
}: {
  item: CartItem;
  onRemove: () => void;
  onUpdateQty: (qty: number) => void;
}) {
  return (
    <div className="flex gap-3 items-start py-2">
      {/* Image */}
      <div className="size-16 rounded-md bg-muted overflow-hidden shrink-0">
        {item.image ? (
          // eslint-disable-next-line @next/next/no-img-element
          <img
            src={item.image}
            alt={item.productTitle}
            className="size-full object-cover"
          />
        ) : (
          <div className="size-full flex items-center justify-center">
            <ShoppingBag className="size-5 text-muted-foreground" aria-hidden="true" />
          </div>
        )}
      </div>

      {/* Info */}
      <div className="flex-1 min-w-0 space-y-1">
        <p className="text-sm font-medium line-clamp-1">{item.productTitle}</p>
        <p className="text-xs text-muted-foreground">
          {item.priceCredits} créditos c/u
        </p>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="icon"
            className="size-7"
            onClick={() => onUpdateQty(item.quantity - 1)}
            disabled={item.quantity <= 1}
            aria-label="Reducir cantidad"
          >
            <Minus className="size-3" aria-hidden="true" />
          </Button>
          <span className="text-sm font-medium w-6 text-center">
            {item.quantity}
          </span>
          <Button
            variant="outline"
            size="icon"
            className="size-7"
            onClick={() => onUpdateQty(item.quantity + 1)}
            aria-label="Aumentar cantidad"
          >
            <Plus className="size-3" aria-hidden="true" />
          </Button>
          <span className="text-sm font-semibold ml-auto">
            {(parseFloat(item.priceCredits) * item.quantity).toFixed(2)} cr
          </span>
          <Button
            variant="ghost"
            size="icon"
            className="size-7 text-destructive hover:text-destructive"
            onClick={onRemove}
            aria-label="Eliminar del carrito"
          >
            <Trash2 className="size-3" aria-hidden="true" />
          </Button>
        </div>
      </div>
    </div>
  );
}
